import React, { useState, useEffect, createContext, useContext } from 'react';
import { API_ORIGIN } from './config';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import Cookies from 'js-cookie';
import axios from 'axios';

// Theme
import theme from './theme';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import AdminHomepageCustomization from './pages/AdminHomepageCustomization';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import BrowsePage from './pages/BrowsePage';
import StatisticsPage from './pages/StatisticsPage';
import AlumniRegistrationPage from './pages/AlumniRegistrationPage';
import BusinessSignUpPage from './pages/BusinessSignUpPage';
import AdminAddRequestsPage from './pages/AdminAddRequestsPage';
import AdminBusinessRequestsPage from './pages/AdminBusinessRequestsPage';
import AdminDataEntryPage from './pages/AdminDataEntryPage';
import AlumniIDCardPage from './pages/AlumniIDCardPage';
import AlumniProfilePage from './pages/AlumniProfilePage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import ContactPage from './pages/ContactPage';
import AdminNewsManagementPage from './pages/AdminNewsManagementPage';
import AdminFormCustomizationPage from './pages/AdminFormCustomizationPage';
import AdminNetworkNotificationsPage from './pages/AdminNetworkNotificationsPage';
import AdminSOPPage from './pages/AdminSOPPage';
import AdminActivityLogPage from './pages/AdminActivityLogPage';
import AdminAlumniIdCleanupPage from './pages/AdminAlumniIdCleanupPage';
const API_BASE = API_ORIGIN;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Remounts ErrorBoundary on every route change (key={pathname}) so a crash on
// one page doesn't permanently lock the whole app — navigating elsewhere via
// the Navbar (which stays outside the boundary) clears the error.
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auto-login on mount
  useEffect(() => {
    autoLogin();
  }, []);

  // ✅ FIXED: Better token refresh logic
  const autoLogin = async () => {
    try {
      console.log('[AUTH] Attempting auto-login...');

      const idToken = Cookies.get('idToken');
      const refreshToken = Cookies.get('refreshToken');

      // If we have a valid idToken, try to use it first
      if (idToken) {
        console.log('[AUTH] Found idToken, verifying...');

        try {
          // Quick verification call to check if token is still valid
          const verifyResponse = await axios.get(`${API_BASE}/api/auth/verify`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });

          if (verifyResponse.data.success) {
            console.log('[AUTH] ✅ Token valid, user logged in');
            setUser({
              uid: verifyResponse.data.uid,
              email: verifyResponse.data.email,
              role: verifyResponse.data.role,
              idToken: idToken
            });
            setLoading(false);
            return;
          }
        } catch (verifyError) {
          console.log('[AUTH] idToken expired or invalid, trying refresh...');
        }
      }

      // If idToken failed or doesn't exist, try refresh token
      if (refreshToken) {
        console.log('[AUTH] Attempting token refresh...');

        try {
          const response = await axios.post(`${API_BASE}/api/auth/refresh`, {
            refreshToken: refreshToken
          });

          if (response.data.success) {
            console.log('[AUTH] ✅ Token refreshed successfully');

            const userData = {
              uid: response.data.uid,
              email: response.data.email,
              role: response.data.role,
              idToken: response.data.idToken
            };

            setUser(userData);

            // Update cookies with new tokens
            Cookies.set('idToken', response.data.idToken, { expires: 1 });
            if (response.data.refreshToken) {
              Cookies.set('refreshToken', response.data.refreshToken, { expires: 30 });
            }

            setLoading(false);
            return;
          }
        } catch (refreshError) {
          console.log('[AUTH] ❌ Token refresh failed:', refreshError.message);
        }
      }

      // No valid tokens - user needs to log in
      console.log('[AUTH] No valid tokens found, user must login');
      Cookies.remove('idToken');
      Cookies.remove('refreshToken');

    } catch (error) {
      console.error('[AUTH] Auto-login error:', error);
      Cookies.remove('idToken');
      Cookies.remove('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    console.log('[AUTH] User logging in:', userData.email);
    setUser({
      uid: userData.uid,
      email: userData.email,
      role: userData.role,
      idToken: userData.idToken
    });
    Cookies.set('idToken', userData.idToken, { expires: 1, sameSite: 'Lax' });
    Cookies.set('refreshToken', userData.refreshToken, { expires: 30, sameSite: 'Lax' });
  };

  const logout = () => {
    console.log('[AUTH] User logging out');
    setUser(null);
    Cookies.remove('idToken');
    Cookies.remove('refreshToken');
  };

  const authValue = {
    user,
    loading,
    login,
    logout,
    setUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'Admin',
    isAlumni: user?.role === 'Alumni',
    isViewOnly: user?.role === 'View Only'
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContext.Provider value={authValue}>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />

            <Box component="main" sx={{ flexGrow: 1, pt: 2, pb: 4 }}>
              <RouteErrorBoundary>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register/alumni" element={<AlumniRegistrationPage />} />
                <Route path="/register/business" element={<BusinessSignUpPage />} />
                <Route path="/contact" element={<ContactPage />} />

                {/* Protected Routes - Authenticated Users */}
                <Route
                  path="/statistics"
                  element={
                    <ProtectedRoute>
                      <StatisticsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes - Admin & View Only */}
                <Route
                  path="/database"
                  element={
                    <ProtectedRoute allowedRoles={['Admin', 'View Only', 'Business']}>
                      <BrowsePage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes - Alumni Only */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute allowedRoles={['Alumni']}>
                      <AlumniProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/alumni-id"
                  element={
                    <ProtectedRoute allowedRoles={['Alumni']}>
                      <AlumniIDCardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes - Admin Only */}
                <Route
                  path="/admin/add-requests"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminAddRequestsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/business-requests"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminBusinessRequestsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/data-entry"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminDataEntryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminUserManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/news"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminNewsManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customize-homepage"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminHomepageCustomization />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/form-options"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminFormCustomizationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/network"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminNetworkNotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/sops"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminSOPPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/activity-log"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminActivityLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/alumni-id-cleanup"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <AdminAlumniIdCleanupPage />
                    </ProtectedRoute>
                  }
                />

                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </RouteErrorBoundary>
            </Box>

            <Footer />
          </Box>
        </Router>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
export { AuthContext };